import { uid } from "@dhx/ts-common/core";
import { IEventSystem } from "@dhx/ts-common/events";
import {
	ChartEvents,
	DrawPoint,
	IScale,
	ISeria,
	ISeriaConfig,
	PointData,
	PointType,
	ScaleType,
	SvgElement,
	TooltipType,
} from "../types";

import { DataCollection } from "@dhx/ts-data";
import { euclideanDistance } from "../helpers/common";
import { getShadeHelper } from "../shapes/line";

export default abstract class BaseSeria implements ISeria {
	public id: string;
	public config: ISeriaConfig;

	protected _events: IEventSystem<ChartEvents>;
	protected _handlers = {
		onclick: (id: string, value: string) => this._events.fire(ChartEvents.serieClick, [id, value]),
		onmousemove: (id: string, value: string, e) =>
			this._events.fire(ChartEvents.seriaMouseMove, [id, value, e]),
		onmouseleave: (id: string, value: string) =>
			this._events.fire(ChartEvents.seriaMouseLeave, [id, value]),
	};

	protected _points: PointData[];
	protected _drawPointType: DrawPoint;

	constructor(
		protected _data: DataCollection<any>,
		config: ISeriaConfig,
		other: IEventSystem<ChartEvents>
	) {
		this.id = config.id = config.id || uid();
		this._events = other;
		this._points = [];

		this._setDefaults(config);
	}

	toggle(): void {
		this.config.active = !this.config.active;
	}

	getClosest(x: number, y: number): [number, number, number, string] {
		const res: [number, number, number, string] = [Infinity, null, null, null];
		for (const point of this._points) {
			const dist = this._getClosestDist(x, y, point[0], point[1]);
			if (res[0] > dist) {
				res[0] = dist;
				res[1] = point[0];
				res[2] = point[1];
				res[3] = point[2];
			}
		}
		return res;
	}

	getClosestVertical(x: number): [number, number, number, string, number] {
		const res: [number, number, number, string, number] = [Infinity, null, null, null, null];
		for (const point of this._points) {
			const dist = Math.abs(point[0] - x);
			if (res[0] > dist) {
				res[0] = dist;
				res[1] = point[0];
				res[2] = point[1];
				res[3] = point[2];
				res[4] = point[4];
			}
		}
		return res;
	}

	getTooltipType(_id: string): TooltipType {
		return "top";
	}

	getTooltipText(id: string) {
		if (!this._data.getItem(id)) {
			return;
		}
		if (this.config.tooltip) {
			const p = this._defaultLocator(this._data.getItem(id));
			if (this.config.tooltipTemplate) {
				return this.config.tooltipTemplate(p);
			}
			return p[0];
		}
	}

	dataReady(prev?: PointData[]): PointData[] {
		return (this._points = []);
	}

	paint(width: number, height: number): SvgElement {
		return this._calckFinalPoints(width, height);
	}

	getPoints(): PointData[] {
		return this._points;
	}

	addScale(type: ScaleType, scale: IScale): void {
		// do nothing
	}

	protected _getClosestDist(x: number, y: number, px: number, py: number): number {
		return euclideanDistance(x, y, px, py);
	}

	protected _calckFinalPoints(_width: number, _height: number) {
		// do nothing
	}

	protected _setDefaults(config) {
		this.config = config;
	}

	protected _defaultLocator(_) {
		return [null, null];
	}

	protected _getPointType(form: PointType, color: string): DrawPoint {
		return getShadeHelper(form, color);
	}
}
